import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Row } from '../../../atoms/row';
import { HelpButton } from '../../../atoms/help-button';
import { SpotPricePeriod } from '../../../../../core/src/shared/enums';
import { headSx } from './spot-summary.styles';

export type RowMessage = { id: string; defaultMessage: string };

type CellProps<T> = {
  value: T;
  periodIndex: number;
  pricePeriod?: SpotPricePeriod;
  tooltip?: string;
};

type Props<T> = {
  title: RowMessage;
  /** When present, renders a help dialog button next to the title. */
  help?: RowMessage;
  data: T[];
  pricePeriod: SpotPricePeriod;
  /** One of the Col* cell components — all share the same prop shape. */
  Cell: (props: CellProps<T>) => React.ReactElement | null;
  getTooltip: (periodIndex: number, pricePeriod: SpotPricePeriod) => string;
};

/**
 * One feature row of the spot summary: a header label (+ optional help) followed
 * by a cell per period. Renders into the parent grid via a fragment.
 */
export function SpotSummaryRow<T>({
  title,
  help,
  data,
  pricePeriod,
  Cell,
  getTooltip,
}: Props<T>) {
  return (
    <>
      <Row sx={headSx}>
        <FormattedMessage {...title} />
        {help && (
          <HelpButton
            variant="dialog"
            title={<FormattedMessage {...title} />}
            content={<FormattedMessage {...help} />}
          />
        )}
      </Row>
      {data.map((value, i) => (
        <Cell
          key={i}
          value={value}
          periodIndex={i}
          pricePeriod={pricePeriod}
          tooltip={getTooltip(i, pricePeriod)}
        />
      ))}
    </>
  );
}
